import { Badge } from "src/app/shared/badges/badges.interface";
import { PaginatedData } from "src/app/shared/model/table.interface";

export interface EmployeeListSearchParams {
  phrase?: string;
  pager: {
    limit: number;
    page: number;
  };
  sortBy?:
    | "employeeId"
    | "fullName"
    | "email"
    | "modifiedAt"
    | "createdAt"
    | "signatureInstallationAt";
  order?: string;
  templateId?: string;
  internalTemplateId?: string;
  signatureInstalled?: TriStateCheckbox | null;
  excludedFromSignatures?: TriStateCheckbox | null;
  groupEmail?: string;
  orgUnitPath?: string;
  branchId?: string | null;
  viewType?: ViewType;
}

export interface EmployeePickerSearchParams {
  phrase?: string;
  pager: {
    limit: number;
    page: number;
  };
}

export interface EmployeePickerItem {
  employeeId: string;
  email: string;
  fullName?: string;
  givenName?: string;
  familyName?: string;
}

export enum TriStateCheckbox {
  ALL = "ALL",
  ONLY_ENABLED = "ONLY_ENABLED",
  ONLY_DISABLED = "ONLY_DISABLED",
}

export type EmployeeDataSource =
  "GOOGLE" | "MICROSOFT" | "LOCAL" | "SAMPLE" | "CALAMARI";

export interface ChangeExcludedFromSignaturesParams {
  employeeId: string;
  excludedFromSignatures: boolean;
}

export interface EmployeeRequest {
  subject: string;
  body: string;
  emails: string[];
  channel: EmployeeInviteChannel;
}

export type EmployeeInviteChannel = "email";

export interface EmployeeListItem {
  employeeId: string;
  fullName?: string;
  email?: string;
  givenName?: string;
  familyName?: string;
  excludedFromSignatures?: boolean;
  canBeExcludedFromSignatures: boolean;
  signatureInstallationAt?: Date;
  emptyFieldsCount?: number;
  createdAt: Date;
  modifiedAt?: Date;
  templateId?: string | null;
  internalTemplateId?: string | null;
  signatureAccepted?: boolean | null;
  groupEmail: string;
  isAlias?: boolean;
  dataSource?: EmployeeDataSource;
  domainSignatures?: DomainSignature[];
}

export interface DomainSignature {
  domain: string;
  signature: string;
  signatureTemplateId: string;
  installedAt: Date;
}

export interface SingleEmployeeResponse {
  employeeId: string;
  fullName: string;
  email: string;
  emptyFieldsCount: number;
  createdAt: Date;
  modifiedAt?: Date;
  excludedFromSignatures: boolean;
  skypeId: string;
  phoneNumber: string;
  picture: string;
  badges: Badge[];
  localAvatar?: boolean;
  dataSource?: EmployeeDataSource;
}

export interface CurrentUserResponse {
  employeeId?: string;
  name: string;
  isAdmin: boolean;
  email: string;
  emptyFieldsCount: number;
  createdAt: Date;
  modifiedAt?: Date;
  excludedFromSignatures: boolean;
  skypeId: string;
  phoneNumber: string;
  picture: string;
  localAvatar?: boolean;
  signature: string;
  signatureInternal?: string;
  dataSource?: EmployeeDataSource;
  gender?: string | null;
  countryOfOrigin?: string | null;
  givenName?: string;
  familyName?: string;
  fullName?: string;
  thumbnailPhotoUrl?: string;
  dynamicVariables?: Record<string, unknown>;
  bannerDisabled?: boolean;
}

export interface EmployeeVcard {
  note?: string | null;
  validFrom?: Date | string | null;
  validTo?: Date | string | null;
}

export type NoteInterval = { start: number; end: number } | "indefinite";

export type EmployeeVcardValidationError =
  "dateRangeInvalid" | "overlap" | "multipleIndefinite";

export interface EmployeeVcardRowValue {
  note?: string | null;
  validFrom?: Date | string | null;
  validTo?: Date | string | null;
}

export interface Employee {
  employeeId: string;
  fullName?: string;
  givenName?: string;
  familyName?: string;
  localizedProfiles?: Partial<
    Record<
      "ja" | "zh" | "ar" | "ko",
      {
        givenName?: string;
        familyName?: string;
        fullName?: string;
        jobTitle?: string;
      }
    >
  > | null;
  userAbout?: string;
  gender?: string | null;
  countryOfOrigin?: string | null;
  vcard: EmployeeVcard[];
  email?: string;
  jobTitle?: string;
  calendarLink?: string;
  mobileNumber?: string;
  phoneNumber?: string;
  skypeId?: string;
  picture?: string;
  localAvatar?: boolean;
  excludedFromSignatures?: boolean;
  emptyFieldsCount?: number;
  createdAt: Date;
  modifiedAt?: Date;
  signatureInstallationAt?: Date;
  signature?: string;
  templateId?: string | null;
  signatureInternal?: string;
  internalTemplateId?: string | null;
  dynamicVariables: Record<string, unknown>;
  customLinks: Link[];
  handwrittenSignature?: Handwritten[];
  badges: Badge[];
  canBeExcludedFromSignatures: boolean;
  thumbnailPhotoUrl?: string;
  bannerDisabled?: boolean;
  aliases?: EmailAddressAlias[];
  dataSource?: EmployeeDataSource;
  domainSignatures?: DomainSignature[];
  hasEmployee?: boolean;
  branchId?: string | null;
  branchVariables?: Record<string, unknown>;
}

export interface EmailAddressAlias {
  email: string;
  aliasName?: string | null;
  isAlias: boolean;
  fullName?: string;
  signatureTemplateId?: string | null;
  signature?: string | null;
  signatureInternalTemplateId?: string | null;
  signatureInternal?: string | null;
  createdAt?: Date | null;
  modifiedAt?: Date | null;
  signatureInstallationAt?: Date | null;
  signatureAccepted?: boolean | null;
}

interface Link {
  text: string;
  url: string;
}

export interface Handwritten {
  label: string;
  input: string;
  url: string;
}

export interface EmployeesState {
  employees: PaginatedData<EmployeeListItem>;
  params: EmployeeListSearchParams;
  currentEmployee: Employee;
  employee: Employee;
  isLoading: boolean;
}

export interface AppState {
  employees: EmployeesState;
}

export interface EditableEmployee extends Employee {
  isEditing?: boolean;
  isNew?: boolean;
  originalData?: Employee;
  validationErrors?: { [key: string]: string };
  isDeleting?: boolean;
  isSaving?: boolean;
}

export type EmployeeWithAliasFlag = (Employee | EmployeeListItem) & {
  isAlias: boolean;
};

export const defaultEmployeeListPager = {
  limit: 10,
  page: 0,
};

export interface EmployeeDataImportVerificationResult {
  preview?: Array<Record<string, unknown>>;
  data?: Record<string, unknown>;
  warnings?: Array<{ key: WarningKey; value?: unknown }>;
  errors?: Array<{ key: ErrorKey; value?: unknown }>;
  meta?: {
    columns: string[];
    numberOfEmployees: number;
  };
}

export interface EmployeeDataImportResult {
  numberOfInstallations: number;
  numberOfChanges: number;
}

export type ViewType = "EMPLOYEES" | "ALIASES" | "BOTH";

export type WarningKey = "IGNORED_VARIABLES" | "NOT_EXISTING_EMPLOYEES";

export type ErrorKey =
  "NO_EMAIL_FIELD" | "DUPLICATED_EMAIL_FIELD" | "NO_HEADER_ROW" | "NO_DATA";
